import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { SaveSuccessItems } from "ui/Scripts/source/models/common/saveSuccessItems";
import { DefaultHandlerType, EventTypeCategory } from "ui/Scripts/source/models/enums";
import { EventUrgencyClassListItems } from "ui/Scripts/source/models/eventUrgencyClass/eventUrgencyClassListItems";
import { EventUrgencyClassEdit } from "ui/Scripts/source/models/eventUrgencyClass/eventUrgencyClassEdit";
import { ISavedChartPanel } from "../chart/savedChartPanel";
import { IPagePanel } from "../common/pagePanel";
import { CompanyListItems } from "../company/companyListItems";
import { EventChartPropertiesDialogData } from "../event/eventChartPropertiesDialogData";
import { EventDefaultEventHandlers } from "../event/eventDefaultEventHandlers";
import { EventEdit } from "../event/eventEdit";
import { EventEditNew } from "../event/eventEditNew";
import { EventListItems } from "../event/eventListItems";
import { EventPage } from "../event/eventPage";
import { EventPageData } from "../event/eventPageData";
import { EventTypeEdit } from "../eventType/eventTypeEdit";
import { EventTypeNodes } from "../eventType/eventTypeNodes";
import { FrontToDoItems } from "../front/frontToDoItems";
import { DashboardChartData } from "../report/dashboardChartData";
import { ReportGenerated2 } from "../report/reportGenerated2";
import { ReportItems } from "../report/reportItems";
import { IReportParameterValue } from "../report/reportParameterValue";
import { ModuleUnits } from "../unit/moduleUnits";
import { UnitNodeDefaultHandlers } from "../unit/unitNodeDefaultHandlers";
import { UnitNodePermissions } from "../unit/unitNodePermissions";
import { UserListItems } from "../user/userListItems";
import { AbortDictionary, getApiCall, getFileApiCall, postApiCall } from "./tokenService";
import { EventSetting } from "ui/Scripts/source/models/eventSettings/eventSetting";
import { UnitModuleSaveResult } from "../unit/unitModuleSaveResult";
import { ModulePartnerListItems } from "../modulePartner/modulePartnerListItems";
import { ModulePartnerEdit } from "../modulePartner/modulePartnerEdit";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { SharedPanels } from "../panel/sharedPanels";
import { ISharedPanelSave } from "../panel/sharedPanel";

//Abort
const commonAborts = new AbortDictionary();
const reportAborts = new AbortDictionary();
const chartAborts = new AbortDictionary();
const statisticAborts = new AbortDictionary();

//App Front
export const getChartData = (companyId: string): Promise<DashboardChartData> => {
    const operationId = "getFrontPageChartData";
    chartAborts.abort(operationId);
    return postApiCall("api/event/chartdata", DashboardChartData, JSON.stringify({
        companyId
    }), (abortId: string) => {
        chartAborts.add(operationId, abortId);
    }).finally(() => {
        chartAborts.remove(operationId);
    });
};

export const getEventChartPropertiesDialogData = (): Promise<EventChartPropertiesDialogData> => {
    return getApiCall("api/event/chartpropertiesdialogdata", EventChartPropertiesDialogData);
};

export const saveEventChartOptions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/event/savechartoptions", ApiSuccess, JSON.stringify(obj));
};

//Page & List
// #region EventPage
export const getEventPage = (): Promise<EventPage> => {
    return getApiCall("api/event", EventPage);
};

export const getEventPageData = (companyId: string): Promise<EventPageData> => {
    return getApiCall("api/event/pagedata?id=" + companyId, EventPageData);
};

export const getEventListItems = (companyId: string, pageSize: number, page: number, filter: string, startDate: number, endDate: number, unitIds: string[],
    states: number[], urgencyClassIds: string[], eventTypeIds: string[], eventTypeBIds: string[], eventTypeCIds: string[], partnerCompanyIds: string[], createUserCodes: string[],
    handlerUserCodes: string[], observers: string[], dynamicTime: number, sortColumn: string, sortOrderIsAsc: boolean): Promise<EventListItems> => {
    commonAborts.abortAll();
    reportAborts.abortAll();
    chartAborts.abortAll();
    statisticAborts.abortAll();
    return postApiCall("api/event/list", EventListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        startDate,
        endDate,
        filter: filter || "",
        unitIds,
        states,
        urgencyClassIds,
        eventTypeIds,
        eventTypeBIds,
        eventTypeCIds,
        partnerCompanyIds,
        createUserCodes,
        handlerUserCodes,
        observers,
        dynamicTime
    }));
};
// #endregion EventPage

// #region Edit
export const getEventEditNew = (companyId: string): Promise<EventEditNew> => {
    return getApiCall("api/event/new?id=" + companyId, EventEditNew);
};

export const saveEventEditNew = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/editsavenew", SaveSuccess, JSON.stringify(obj));
};

export const getEventEdit = (companyId: string, id: string, lockEvent: boolean): Promise<EventEdit> => {
    return getApiCall("api/event/edit?id=" + id + "&companyId=" + companyId + "&lockEvent=" + lockEvent, EventEdit);
};

export const saveEventEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/editsave", SaveSuccess, JSON.stringify(obj));
};

export const sendEventReadReceipt = (companyId: string, id: string): Promise<ApiSuccess> => {
    return postApiCall("api/event/sendreadreceipts", ApiSuccess, JSON.stringify({
        companyId,
        id
    }));
};

export const removeEvent = (companyId: string, createCompanyId: string, id: string): Promise<ApiSuccess> => {
    return postApiCall("api/event/remove?id=" + id + "&createCompanyId=" + createCompanyId + "&companyId=" + companyId, ApiSuccess);
};

export const getDefaultEventHandlersForUnit = (companyId: string, createCompanyId: string, unitId: string, isNewEvent: boolean): Promise<EventDefaultEventHandlers> => {
    return getApiCall("api/event/unitdefaulthandlers?companyId=" + companyId + "&createCompanyId=" + createCompanyId + "&unitId=" + unitId + "&isNewEvent=" + isNewEvent.toString(), EventDefaultEventHandlers);
};

export const getEventPrint = (companyId: string, id: string, printId: string): Promise<string> => {
    return getFileApiCall("api/event/print?id=" + id + "&companyId=" + companyId + "&printId=" + printId);
};
// #endregion Edit

// #region Handlers
export const approveEvent = (companyId: string, createCompanyId: string, eventId: string): Promise<ApiSuccess> => {
    return postApiCall("api/event/approve", ApiSuccess, JSON.stringify({ companyId, createCompanyId, eventId }));
};

export const getHandlersSelectUserListItems = (companyId: string, eventId: string, unitId: string, pageSize: number, page: number, filter: string,
    ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, doNotIgnoreRecordUserConnectedUserCode: string,
    sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
    return postApiCall("api/event/selecthandlers", UserListItems, JSON.stringify({
        companyId,
        eventId,
        unitId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        ignoreUserIds,
        ignoreUserCodes,
        ignoreRecordUserConnected,
        doNotIgnoreRecordUserConnectedUserCode,
        activityState
    }));
};

export const eventEditAddHandlers = (companyId: string, eventId: string, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/event/editaddhandlers", ApiSuccess, JSON.stringify({
        companyId,
        eventId,
        userCodes
    }));
};

export const eventEditRemoveHandlers = (companyId: string, eventId: string, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/event/editremovehandlers", ApiSuccess, JSON.stringify({
        companyId,
        eventId,
        userCodes
    }));
};
// #endregion Handlers

// #region Edit Message
export const eventEditAddMessage = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/editaddmessage", SaveSuccess, JSON.stringify(obj));
};

export const sendEventMessageReadReceipts = (companyId: string, eventId: string, ids: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/event/sendmessagereadreceipts", ApiSuccess, JSON.stringify({
        companyId,
        eventId,
        ids
    }));
};
// #endregion Edit Message

// #region Edit EventTypes
export const eventEditSaveEventTypes = (companyId: string, eventId: string, category: EventTypeCategory, ids: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/event/editasaveeventtypes", ApiSuccess, JSON.stringify({
        companyId,
        eventId,
        category,
        ids
    }));
};
// #endregion Edit EventTypes

// #region Edit Attachment
export const eventEditRemoveAttachments = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/event/editremoveattachments", ApiSuccess, JSON.stringify(obj));
};

export const eventEditAddAttachments = (obj: any): Promise<SaveSuccessItems> => {
    return postApiCall("api/event/editaddattachments", SaveSuccessItems, JSON.stringify(obj));
};
// #endregion Edit Attachment

// #region Report
export const getEventPageReportItems = (companyId: string): Promise<ReportItems> => {
    const operationId = "getEventPageReportItems_" + (companyId ?? "");
    commonAborts.abort(operationId);
    return getApiCall("api/event/reports?id=" + (companyId ?? ""), ReportItems, (id: string) => {
        commonAborts.add(operationId, id);
    }).finally(() => {
        commonAborts.remove(operationId);
    });
};

export const getReportGenerated = (companyId: string, reportId: string, reportName: string, startDate: number, endDate: number, dynamicTime: number, unitIds: string[],
    states: number[], urgencyClassIds: string[], eventTypeIds: string[], eventTypeBIds: string[], eventTypeCIds: string[], partnerCompanyIds: string[], createUserCodes: string[],
    handlerUserCodes: string[], observers: string[], parameterValues: IReportParameterValue[]): Promise<ReportGenerated2> => {
    reportAborts.abortAll();
    return postApiCall("api/event/createreport", ReportGenerated2, JSON.stringify({
        companyId,
        reportId,
        reportName,
        startDate,
        endDate,
        dynamicTime,
        unitIds,
        states,
        urgencyClassIds,
        eventTypeIds,
        eventTypeBIds,
        eventTypeCIds,
        partnerCompanyIds,
        createUserCodes,
        handlerUserCodes,
        observers,
        parameterValues
    }), (id: string) => {
        reportAborts.add(reportId, id);
    }).finally(() => {
        reportAborts.remove(reportId);
    });
};

export const getChart = (panelId: string, companyId: string, reportId: string, chartId: string, startDate: number, endDate: number, dynamicTime: number, unitIds: string[],
    states: number[], urgencyClassIds: string[], eventTypeIds: string[], eventTypeBIds: string[], eventTypeCIds: string[], partnerCompanyIds: string[], createUserCodes: string[],
    handlerUserCodes: string[], observers: string[], parameterValues: IReportParameterValue[]): Promise<DashboardChartData> => {
    chartAborts.abort(panelId);
    reportAborts.abortAll();
    return postApiCall("api/event/createchart", DashboardChartData, JSON.stringify({
        companyId,
        reportId,
        chartId,
        startDate,
        endDate,
        dynamicTime,
        unitIds,
        states,
        urgencyClassIds,
        eventTypeIds,
        eventTypeBIds,
        eventTypeCIds,
        partnerCompanyIds,
        createUserCodes,
        handlerUserCodes,
        observers,
        parameterValues
    }), (abortId: string) => {
        chartAborts.add(panelId, abortId);
    }).finally(() => {
        chartAborts.remove(panelId);
    });
};
// #endregion Report

// #region Dashboard
export const saveEventPanels = (panels: IPagePanel[]): Promise<ApiSuccess> => {
    return postApiCall("api/event/savepanels", ApiSuccess, JSON.stringify({ panels }));
};

export const saveEventChartPanels = (panels: ISavedChartPanel[]): Promise<ApiSuccess> => {
    return postApiCall("api/event/savechartpanels", ApiSuccess, JSON.stringify({ panels }));
};

export const getEventToDoItems = (): Promise<FrontToDoItems> => {
    const operationId = "getEventToDoItems";
    statisticAborts.abort(operationId);
    return getApiCall("api/event/todoitems", FrontToDoItems, (id: string) => {
        statisticAborts.add(operationId, id);
    }).finally(() => {
        statisticAborts.remove(operationId);
    });
};

export const getSharedPanels = (companyId: string): Promise<SharedPanels> => {
    return getApiCall("api/event/sharedpanels?id=" + companyId, SharedPanels);
};

export const saveSharedPanel = (companyId: string, orgSharedPanelId: string, panel: ISharedPanelSave): Promise<ApiSuccess> => {
    return postApiCall("api/event/savesharedpanel", ApiSuccess, JSON.stringify({ companyId, orgSharedPanelId, panel }));
};

export const getSharePanelUserGroupListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
    ignoreUserGroupCodes: string[], requireUserGroupCodes: string[]): Promise<UserGroupListItems> => {
    return postApiCall("api/event/getsharepanelusergrouplistitems", UserGroupListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        groupType: groupType || 0,
        ignoreUserGroupCodes: ignoreUserGroupCodes || [],
        requireUserGroupCodes: requireUserGroupCodes || []
    }));
};
// #endregion Dashboard

// #region Module Permissions
export const getUnitNodePermissions = (companyId: string): Promise<UnitNodePermissions> => {
    return getApiCall("api/event/module/permissions?id=" + companyId, UnitNodePermissions);
};

export const saveUnitNodePermissions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/event/module/savepermissions", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module Permissions

// #region Module units
export const getUnits = (companyId: string): Promise<ModuleUnits> => {
    return getApiCall("api/event/module/units?id=" + companyId, ModuleUnits);
};

export const saveUnits = (obj: any): Promise<UnitModuleSaveResult> => {
    return postApiCall("api/event/module/saveunits", UnitModuleSaveResult, JSON.stringify(obj));
};
// #endregion Module units

// #region DefaultHandlers
export const getUnitNodeDefaultHandlers = (companyId: string): Promise<UnitNodeDefaultHandlers> => {
    return getApiCall("api/event/module/defaulthandlers?id=" + companyId, UnitNodeDefaultHandlers);
};

export const saveUnitNodeDefaultHandlers = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/event/module/savedefaulthandlers", ApiSuccess, JSON.stringify(obj));
};

export const defaultHandlersGetSelectUserListItems = (companyId: string, unitId: string, handlerType: DefaultHandlerType, pageSize: number, page: number, filter: string,
    ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, doNotIgnoreRecordUserConnectedUserCode: string,
    sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
    return postApiCall("api/event/module/defaulthandlersselectuserslist", UserListItems, JSON.stringify({
        companyId,
        unitId,
        handlerType,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        ignoreUserIds,
        ignoreUserCodes,
        ignoreRecordUserConnected,
        doNotIgnoreRecordUserConnectedUserCode,
        activityState
    }));
};
// #endregion DefaultHandlers

// #region EventTypes
export const getEventTypes = (companyId: string, category: number): Promise<EventTypeNodes> => {
    return postApiCall("api/event/module/eventtype/tree", EventTypeNodes, JSON.stringify({
        companyId,
        category
    }));
};

export const getEventTypeEdit = (companyId: string, category: number, eventTypeId: string): Promise<EventTypeEdit> => {
    return getApiCall("api/event/module/eventtype/edit?id=" + eventTypeId + "&companyId=" + companyId + "&category=" + category.toString(10), EventTypeEdit);
};

export const saveEventTypeEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/module/eventtype/save", SaveSuccess, JSON.stringify(obj));
};

export const removeEventType = (companyId: string, category: number, id: string, code: string): Promise<ApiSuccess> => {
    return postApiCall("api/event/module/eventtype/remove", ApiSuccess, JSON.stringify({
        companyId,
        category,
        id,
        code
    }));
};
// #endregion EventTypes

// #region EventPartners
export const getEventPartnerListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<ModulePartnerListItems> => {
    return postApiCall("api/event/module/eventpartner/list", ModulePartnerListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getEventPartnerEdit = (companyId: string, eventPartnerId: string): Promise<ModulePartnerEdit> => {
    return getApiCall("api/event/module/eventpartner/edit?id=" + eventPartnerId + "&companyId=" + companyId, ModulePartnerEdit);
};

export const saveEventPartnerEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/module/eventpartner/save", SaveSuccess, JSON.stringify(obj));
};

export const removeEventPartner = (companyId: string, id: string, code: string): Promise<ApiSuccess> => {
    return postApiCall("api/event/module/eventpartner/remove", ApiSuccess, JSON.stringify({
        companyId,
        id,
        code
    }));
};

export const getSelectPartnerCompaniesListItems = (pageSize: number, page: number, filter: string, companyGroupIds: string[], activityState: number, sortColumn: string, sortOrderIsAsc: boolean, ignoreCompanyGroupId: string, ignoreCompanyIds: string[]): Promise<CompanyListItems> => {
    return postApiCall("api/event/module/eventpartner/selectcompanieslist", CompanyListItems, JSON.stringify({
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        companyGroupIds,
        activityState,
        ignoreCompanyGroupId,
        ignoreCompanyIds
    }));
};
// #endregion EventPartners

// #region Module EventUrgencyClasses
export const getEventUrgencyClassListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<EventUrgencyClassListItems> => {
    return postApiCall("api/event/module/urgencyclass/list", EventUrgencyClassListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getEventUrgencyClassEdit = (companyId: string, eventUrgencyClassId: string): Promise<EventUrgencyClassEdit> => {
    return getApiCall("api/event/module/urgencyclass/edit?id=" + eventUrgencyClassId + "&companyId=" + companyId, EventUrgencyClassEdit);
};

export const saveEventUrgencyClassEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/module/urgencyclass/save", SaveSuccess, JSON.stringify(obj));
};

export const removeEventUrgencyClasses = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/event/module/urgencyclass/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module EventUrgencyClasses

// #region Module EventSettings
export const getEventSetting = (companyId: string): Promise<EventSetting> => {
    return getApiCall("api/event/module/settings/edit?id=" + (companyId ?? ""), EventSetting);
};

export const saveEventSetting = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/event/module/settings/save", SaveSuccess, JSON.stringify(obj));
};
// #endregion Module EventSettings